import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { printTicketBlob } from "@/lib/ticketPrinter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Receipt, Printer, FileText } from "lucide-react";
import { toast } from "sonner";

const emptyLine = { description: "", quantity: 1, unit_price: 0, iva_rate: 21, discount_percent: 0 };

export default function QuickInvoice() {
  const { user, centers } = useAuth();
  const [centerId, setCenterId] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [customerDni, setCustomerDni] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [items, setItems] = useState([{ ...emptyLine }]);
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [invDate, setInvDate] = useState(new Date().toISOString().slice(0, 10));
  const [stockItems, setStockItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (["admin", "center_admin"].includes(user?.role) && centers?.length && !centerId) {
      setCenterId(centers[0].id);
    } else if (user?.center_id && !centerId) {
      setCenterId(user.center_id);
    }
  }, [user, centers, centerId]);

  useEffect(() => {
    if (!centerId) return;
    api.get("/stock/available", { params: { center_id: centerId } })
      .then((r) => setStockItems((r.data || []).filter((s) => (s.quantity || 0) > 0 && !s.is_trial)))
      .catch(() => setStockItems([]));
  }, [centerId]);

  // Precios con IVA INCLUIDO: desglose hacia atrás
  const lineGross = (it) => {
    const d = Math.max(0, Math.min(100, parseFloat(it.discount_percent) || 0));
    return (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0) * (1 - d / 100);
  };
  const total = items.reduce((a, it) => a + lineGross(it), 0);
  const ivaByRate = {};
  items.forEach((it) => {
    const g = lineGross(it);
    const r = parseFloat(it.iva_rate) || 0;
    ivaByRate[r] = (ivaByRate[r] || 0) + (g - g / (1 + r / 100));
  });
  const ivaTotal = Object.values(ivaByRate).reduce((a, b) => a + b, 0);
  const subtotal = total - ivaTotal;

  const setLine = (idx, key, v) => {
    const copy = [...items];
    copy[idx] = { ...copy[idx], [key]: v };
    setItems(copy);
  };

  const pickStock = (idx, sid) => {
    const s = stockItems.find((x) => x.id === sid);
    if (!s) return;
    const copy = [...items];
    copy[idx] = {
      description: `${s.brand || ""} ${s.model || ""}`.trim() || s.description || s.type,
      quantity: 1,
      unit_price: s.unit_price || 0,
      iva_rate: s.type === "audifono" ? 10 : 21,
      discount_percent: 0,
    };
    setItems(copy);
  };

  const reset = () => {
    setCustomerName("");
    setCustomerDni("");
    setCustomerPhone("");
    setCustomerAddress("");
    setItems([{ ...emptyLine }]);
    setPaymentMethod("efectivo");
    setNotes("");
    setLastInvoice(null);
  };

  const submit = async (autoTicket = false) => {
    if (!centerId) {
      toast.error("Selecciona un centro");
      return;
    }
    const cleaned = items.filter((i) => (i.description || "").trim() && parseFloat(i.unit_price) > 0);
    if (cleaned.length === 0) {
      toast.error("Añade al menos una línea con descripción y precio");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/invoices/quick", {
        center_id: centerId,
        date: invDate || undefined,
        customer_name: (customerName || "Cliente varios").trim(),
        customer_dni: customerDni.trim(),
        customer_phone: customerPhone.trim(),
        customer_address: customerAddress.trim(),
        items: cleaned.map((i) => ({
          description: i.description,
          quantity: parseFloat(i.quantity) || 1,
          unit_price: parseFloat(i.unit_price) || 0,
          iva_rate: parseFloat(i.iva_rate) || 0,
          discount_percent: Math.max(0, Math.min(100, parseFloat(i.discount_percent) || 0)),
        })),
        payment_method: paymentMethod,
        iva_rate: 21,
        notes,
      });
      setLastInvoice(data);
      toast.success(`Factura ${data.invoice_number} emitida (${data.total.toFixed(2)} €)`);
      if (autoTicket) {
        // Imprimir/abrir ticket automáticamente
        try {
          const resp = await api.get(`/invoices/${data.id}/ticket`, { responseType: "blob" });
          try {
            if (await printTicketBlob(resp.data)) {
              toast.success("Ticket enviado a la impresora");
              return;
            }
          } catch (perr) {
            toast.error(`Impresora: ${perr.message}. Abriendo PDF…`);
          }
          const url = URL.createObjectURL(resp.data);
          window.open(url, "_blank");
        } catch (e) {
          toast.error("No se pudo abrir el ticket: " + formatApiError(e));
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openTicket = async () => {
    if (!lastInvoice) return;
    try {
      const resp = await api.get(`/invoices/${lastInvoice.id}/ticket`, { responseType: "blob" });
      try {
        if (await printTicketBlob(resp.data)) {
          toast.success("Ticket enviado a la impresora");
          return;
        }
      } catch (perr) {
        toast.error(`Impresora: ${perr.message}. Abriendo PDF…`);
      }
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openPdf = async () => {
    if (!lastInvoice) return;
    try {
      const resp = await api.get(`/invoices/${lastInvoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="quick-invoice-page">
      <div>
        <div className="text-xs tracking-[0.15em] uppercase text-[#6E7772] mb-1">
          Mostrador
        </div>
        <h1 className="font-heading text-4xl text-[#2A2F2C]">Factura / ticket rápido</h1>
        <p className="text-[#6E7772] mt-2 text-sm">
          Emisión rápida para clientes sin ficha de paciente (cliente varios). Se crea
          una factura igual que las demás, asociada a un cliente &quot;varios&quot; interno.
        </p>
      </div>

      {lastInvoice && (
        <Card className="border-[#C9B79C] bg-[#FBF7EF]" data-testid="last-invoice-card">
          <CardContent className="pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#7C5A2A]">Última factura emitida</div>
              <div className="font-heading text-2xl text-[#2A2F2C] mt-1">
                {lastInvoice.invoice_number} · {lastInvoice.total.toFixed(2)} €
              </div>
              <div className="text-xs text-[#6E7772] mt-1">
                {lastInvoice.customer_name} {lastInvoice.customer_dni ? `· ${lastInvoice.customer_dni}` : ""}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={openTicket} data-testid="ticket-btn" variant="outline" className="border-[#4A5D4E] text-[#4A5D4E]">
                <Printer className="h-4 w-4 mr-2" /> Imprimir ticket 80mm
              </Button>
              <Button onClick={openPdf} data-testid="pdf-btn" variant="outline" className="border-[#4A5D4E] text-[#4A5D4E]">
                <FileText className="h-4 w-4 mr-2" /> Factura A4 PDF
              </Button>
              <Button onClick={reset} variant="ghost">Nueva venta</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-[#E5E3DB] bg-white">
        <CardHeader>
          <CardTitle className="font-heading text-2xl flex items-center gap-2">
            <Receipt className="h-5 w-5 text-[#4A5D4E]" /> Datos del cliente
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {["admin", "center_admin"].includes(user?.role) && (
            <div>
              <Label>Centro *</Label>
              <Select value={centerId} onValueChange={setCenterId}>
                <SelectTrigger data-testid="qi-center"><SelectValue placeholder="Centro" /></SelectTrigger>
                <SelectContent>
                  {centers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="md:col-span-2">
            <Label>Nombre del cliente</Label>
            <Input
              data-testid="qi-customer-name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>
          <div>
            <Label>DNI / NIF</Label>
            <Input
              data-testid="qi-customer-dni"
              value={customerDni}
              onChange={(e) => setCustomerDni(e.target.value)}
            />
          </div>
          <div>
            <Label>Teléfono</Label>
            <Input
              data-testid="qi-customer-phone"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
            />
          </div>
          <div>
            <Label>Dirección</Label>
            <Input
              data-testid="qi-customer-address"
              value={customerAddress}
              onChange={(e) => setCustomerAddress(e.target.value)}
            />
          </div>
          <div>
            <Label>Fecha de la factura</Label>
            <Input
              type="date"
              data-testid="qi-invoice-date"
              value={invDate}
              onChange={(e) => setInvDate(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] bg-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-2xl">Productos / servicios</CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setItems([...items, { ...emptyLine }])}
            data-testid="qi-add-line"
          >
            <Plus className="h-4 w-4 mr-1" /> Añadir línea
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E3DB]">
                <TableHead className="w-[38%]">Descripción</TableHead>
                <TableHead className="w-[18%]">Stock (opcional)</TableHead>
                <TableHead className="w-12">Uds</TableHead>
                <TableHead className="w-24">Precio €</TableHead>
                <TableHead className="w-16">Dto %</TableHead>
                <TableHead className="w-16">IVA %</TableHead>
                <TableHead className="w-24 text-right">Total</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it, idx) => {
                const base = lineGross(it);
                return (
                  <TableRow key={idx} className="border-[#E5E3DB]">
                    <TableCell>
                      <Input
                        value={it.description}
                        onChange={(e) => setLine(idx, "description", e.target.value)}
                        data-testid={`qi-line-desc-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Select onValueChange={(v) => pickStock(idx, v)}>
                        <SelectTrigger className="text-xs h-8 bg-transparent border-dashed">
                          <SelectValue placeholder="Insertar desde stock…" />
                        </SelectTrigger>
                        <SelectContent>
                          {stockItems.length === 0 && (
                            <SelectItem value="__empty__" disabled>Sin stock disponible</SelectItem>
                          )}
                          {stockItems.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {(s.brand || "—") + " " + (s.model || "")} · {s.unit_price?.toFixed?.(2) || "0,00"}€ · qty {s.quantity}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={it.quantity}
                        onChange={(e) => setLine(idx, "quantity", e.target.value)}
                        className="text-right"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={it.unit_price}
                        onChange={(e) => setLine(idx, "unit_price", e.target.value)}
                        data-testid={`qi-line-price-${idx}`}
                        className="text-right"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={it.discount_percent ?? 0}
                        onChange={(e) => setLine(idx, "discount_percent", e.target.value)}
                        className="text-right"
                        title="Descuento % de esta línea"
                        data-testid={`qi-line-discount-${idx}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Select value={String(it.iva_rate)} onValueChange={(v) => setLine(idx, "iva_rate", parseFloat(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[0, 4, 10, 21].map((r) => (
                            <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right font-medium" data-testid={`qi-line-total-${idx}`}>{base.toFixed(2)} €</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setItems(items.filter((_, i) => i !== idx))}
                        disabled={items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-[#C07F67]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] bg-white">
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label>Forma de pago</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger data-testid="qi-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="efectivo">Efectivo</SelectItem>
                <SelectItem value="tarjeta">Tarjeta</SelectItem>
                <SelectItem value="bizum">Bizum</SelectItem>
                <SelectItem value="transferencia">Transferencia</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label>Notas</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Opcional" />
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E5E3DB] bg-white">
        <CardContent className="pt-6 flex flex-col md:flex-row items-end justify-between gap-4">
          <div className="space-y-1 text-right text-sm">
            <div>Base imponible: <strong className="ml-2">{subtotal.toFixed(2)} €</strong></div>
            {Object.entries(ivaByRate).map(([r, amt]) => (
              <div key={r}>IVA {r}% (incluido): <strong className="ml-2">{amt.toFixed(2)} €</strong></div>
            ))}
            <div className="font-heading text-2xl text-[#2A2F2C]" data-testid="qi-grand-total">
              TOTAL: <strong>{total.toFixed(2)} €</strong>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => submit(false)}
              disabled={saving}
              variant="outline"
              data-testid="qi-emit-invoice"
              className="border-[#4A5D4E] text-[#4A5D4E]"
            >
              <FileText className="h-4 w-4 mr-2" /> Emitir factura
            </Button>
            <Button
              onClick={() => submit(true)}
              disabled={saving}
              data-testid="qi-emit-ticket"
              className="bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white"
            >
              <Printer className="h-4 w-4 mr-2" /> Emitir + imprimir ticket
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
